/**
 * Keeps React alive when something outside React rewrites its text nodes.
 *
 * Browser translation (Chrome's built-in translator, Google Translate, Edge)
 * replaces each text node with `<font>` wrappers. React still holds the
 * original node, so its next `insertBefore(x, thatText)` or
 * `removeChild(thatText)` targets a node that is no longer a child, and the
 * DOM throws `NotFoundError`, which unmounts the whole tree
 * (Sentry WEXARE-MVP-1W). Our users are es/pt speakers on browsers that offer
 * translation, so this cannot be prevented by asking them not to.
 *
 * The guard only changes behaviour on calls that would otherwise throw. A
 * correct call goes straight to the native method.
 * Same approach the React team recommends: facebook/react#11538.
 */

export type DomGuardRecovery = "insertBefore" | "removeChild";

const INSTALLED = Symbol.for("wexare.translateDomGuard");

/** The ancestor of `node` that is a direct child of `parent`, if any. */
function childOf(parent: Node, node: Node): Node | null {
  let current: Node | null = node;
  while (current && current.parentNode !== parent) current = current.parentNode;
  return current;
}

export function installTranslateDomGuard(
  onRecover?: (kind: DomGuardRecovery) => void,
): void {
  if (typeof Node !== "function") return;
  const proto = Node.prototype as Node & { [INSTALLED]?: true };
  if (proto[INSTALLED]) return;
  proto[INSTALLED] = true;

  const nativeInsertBefore = proto.insertBefore;
  const nativeRemoveChild = proto.removeChild;

  proto.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      onRecover?.("insertBefore");
      // Wrapped in place: insert before the wrapper so order is kept.
      // Detached: the position is gone, appending beats dropping the node.
      const anchor = childOf(this, reference);
      return nativeInsertBefore.call(this, newNode, anchor) as T;
    }
    return nativeInsertBefore.call(this, newNode, reference) as T;
  };

  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      onRecover?.("removeChild");
      // Wrapped: take the node out of its wrapper. Detached: already gone.
      if (child.parentNode && this.contains(child)) {
        nativeRemoveChild.call(child.parentNode, child);
      }
      return child;
    }
    return nativeRemoveChild.call(this, child) as T;
  };
}
